import {
  DeliveryGift,
  DeliveryGiftCard,
  Order,
  OrderDelivery,
  OrderGift,
  OrderGiftCardProductEmpty,
  OrderGiftCardProductRetain24,
  OrderLine,
  ShippingFee
} from "lib/types/generated/graphql-types"
import { OrderOperations } from "./OrderOperations"

export class Delivery extends OrderOperations {
  constructor(delivery: OrderDelivery[]) {
    super(delivery)
  }

  public completedDeliveries(): OrderDelivery[] {
    return this.completedList() as OrderDelivery[]
  }

  public failedDeliveries(): OrderDelivery[] {
    return this.failedList() as OrderDelivery[]
  }

  public getOngoingDelivery(): OrderDelivery {
    const onGoingList = this.onGoingList() as OrderDelivery[]
    return onGoingList[0]
  }

  public getPendingDelivery(): OrderDelivery {
    const pendingList = this.pendingList() as OrderDelivery[]
    return pendingList[0]
  }

  public giftsDelivered(giftId: string): (DeliveryGift | undefined)[] {
    return this.completedDeliveries().map((delivery) =>
      delivery.gifts.find((deliveryGift) => deliveryGift.giftId === giftId)
    )
  }

  public giftCardsInDeliveries(): DeliveryGiftCard[] {
    return this.completedDeliveries().flatMap((delivery) => delivery.giftCards)
  }

  public paymentTotal(): number {
    return this.completedDeliveries()
      .map((delivery) => delivery.totals.grandTotal)
      .reduce((a, b) => a + b, 0)
  }

  public giftCardTotal(): number {
    return this.completedDeliveries()
      .map((delivery) => delivery.totals.giftCardTotal)
      .reduce((a, b) => a + b, 0)
  }

  public bonusTotal(): number {
    return this.completedDeliveries()
      .map((delivery) => delivery.totals.bonusTotal)
      .reduce((a, b) => a + b, 0)
  }

  public getDeliveredQuantity(orderLineId: string): number {
    return this.completedDeliveries()
      .flatMap((delivery) => delivery.orderLines)
      .filter((deliveryOrderLine) => deliveryOrderLine.orderLineId === orderLineId)
      .reduce((sum, deliveryOrderLine) => sum + deliveryOrderLine.quantity, 0)
  }

  public getDeliveredGiftQuantity(giftId: string): number {
    return this.completedDeliveries()
      .flatMap((delivery) => delivery.gifts)
      .filter((deliveryGift) => deliveryGift.giftId === giftId)
      .reduce((sum, deliveryGift) => sum + deliveryGift.quantity, 0)
  }

  public getRemainingOrderLines(orderLines: OrderLine[]): OrderLine[] {
    return orderLines
      .map((orderLine) => {
        const deliveredQuantity = this.getDeliveredQuantity(orderLine.id)
        const remainingQuantity = orderLine.quantity - deliveredQuantity

        if (remainingQuantity <= 0) return null

        return {
          ...orderLine,
          quantity: remainingQuantity
        }
      })
      .filter((ol): ol is OrderLine => ol !== null)
  }

  public orderLineDelivered(orderLineId: string): OrderDelivery | undefined {
    return this.completedDeliveries().find((delivery) =>
      delivery.orderLines.find((ol) => ol.orderLineId === orderLineId)
    )
  }

  public getRemainingGifts(gifts: OrderGift[]): OrderGift[] {
    return gifts
      .map((gift) => {
        const deliveredQuantity = this.getDeliveredGiftQuantity(gift.id)
        const remainingQuantity = gift.quantity - deliveredQuantity

        if (remainingQuantity <= 0) return null

        return {
          ...gift,
          quantity: remainingQuantity
        }
      })
      .filter((ol): ol is OrderGift => ol !== null)
  }

  public isShippingFeeDelivered(shippingFeeId: string): boolean {
    return this.completedDeliveries()
      .flatMap((delivery) => delivery.shippingFees)
      .some((fee) => fee.shippingFeeId === shippingFeeId)
  }

  public getRemainingShippingFees(shippingFees: ShippingFee[]): ShippingFee[] {
    return shippingFees.filter((fee) => {
      if (!fee.id) return true
      return !this.isShippingFeeDelivered(fee.id)
    })
  }

  public isGiftCardProductDelivered(giftCardProductId: string): boolean {
    return this.completedDeliveries()
      .flatMap((delivery) => delivery.giftCardProducts)
      .some((product) => product.giftCardProductId === giftCardProductId)
  }

  public isOrderFullyDelivered(order: Order): boolean {
    if (!order?.GiftCardProduct) {
      return (
        this.getRemainingGifts(order?.gifts ?? []).length === 0 &&
        this.getRemainingOrderLines(order?.orderLines ?? []).length === 0 &&
        this.getRemainingShippingFees(order?.shippingFees ?? []).length === 0
      )
    }

    const remainingGiftCardProducts = this.getRemainingGiftCardProducts(order.GiftCardProduct)
    const hasRemainingGiftCards = remainingGiftCardProducts?.__typename === "OrderGiftCardProductRetain24"
      ? (remainingGiftCardProducts.giftCardProducts?.length ?? 0) > 0
      : false

    return (
      this.getRemainingGifts(order?.gifts ?? []).length === 0 &&
      this.getRemainingOrderLines(order?.orderLines ?? []).length === 0 &&
      this.getRemainingShippingFees(order?.shippingFees ?? []).length === 0 &&
      !hasRemainingGiftCards
    )
  }

  public getRemainingGiftCardProducts(
    giftCardProducts: OrderGiftCardProductEmpty | OrderGiftCardProductRetain24
  ): OrderGiftCardProductEmpty | OrderGiftCardProductRetain24 {
    if (giftCardProducts.__typename === "OrderGiftCardProductEmpty") {
      return giftCardProducts
    }

    const retain24 = giftCardProducts as OrderGiftCardProductRetain24

    return {
      ...retain24,
      giftCardProducts: retain24.giftCardProducts?.filter(
        (product) => !this.isGiftCardProductDelivered(product.id)
      )
    }
  }
}
